// MACHINERY V2 (Stroje) — the fleet's mobile FILTER SHEET (M2.16a).
//
// Rendered truth: mobile/screenshots/02-filter-sheet-open.png. Reference impl:
// mobile/machinery-mobile.jsx MbFilterSheet (:126-164). The CHROME (radius,
// handle, scrim, scroll behaviour, portal rule, propagation guards) is
// mobile/SheetV2.tsx and is shared with the katalog and profile sheets; what is
// here is the fleet's own header action, footer and content.
//
// >>> THE TWO FOOTER VERBS ARE NOT THE SAME BUTTON. <<< "Zavriet" DISCARDS the
// pending FILTER selection; "Zobrazit n strojov" APPLIES it, and its n is a
// PREVIEW of what applying would show — not a description of what is already
// behind the sheet.
//
// >>> AND THEY DO NOT GOVERN THE WHOLE SHEET. <<< Three of the six sections
// (ZOSKUPIT PODLA / ZOBRAZENIE / ROZSAH) apply LIVE, on tap, and neither verb
// touches them: Zavriet does not undo a view change, and the primary does not
// wait to make one. The split, and the ONE file that decides it, is
// useFleetFilterDraft.ts; which section is which is said out loud on the
// section eyebrows (fleetSheetSections.tsx), so this file stays chrome and
// still knows nothing about either model.
//
// Esc and the backdrop take the Zavriet path, because Radix routes all three
// through the same `onOpenChange(false)`. Nothing needs to be discarded
// explicitly — the next open re-seeds the FACETS from applied state (see the
// seam's header), which is also what keeps a re-render or the 60 s telemetry
// tick from ever wiping a selection the user is still making.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2GroupBy } from "../machineryV2UiSlice";
import SheetV2 from "../mobile/SheetV2";
import { SheetTextAction } from "../mobile/sheetAtoms";
import FleetSheetSections from "./fleetSheetSections";
import { FleetPageData } from "./fleetPageData";
import { FleetScope } from "./FleetToolbar";
import { useFleetFilterDraft } from "./useFleetFilterDraft";

export interface FleetFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: FleetPageData;
  scope: FleetScope;
  onScopeChange: (scope: FleetScope) => void;
  /** The page's `changeGroupBy` — see the seam's note on why it may not be the
   *  raw `setFleetGroupBy` action. */
  onGroupByChange: (groupBy: MachineryV2GroupBy) => void;
}

const FleetFilterSheet: React.FC<FleetFilterSheetProps> = ({
  open,
  onOpenChange,
  data,
  scope,
  onScopeChange,
  onGroupByChange,
}) => {
  const { translate } = useTranslation("myMachinery");
  const api = useFleetFilterDraft({ open, data, scope, onScopeChange, onGroupByChange });

  const apply = () => {
    api.commit();
    onOpenChange(false);
  };

  return (
    <SheetV2
      open={open}
      onOpenChange={onOpenChange}
      title={translate("myMachinery:v2.mobile.sheetTitle")}
      headerAction={
        <SheetTextAction
          label={translate("myMachinery:v2.mobile.clearAll")}
          onClick={api.clearAll}
        />
      }
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={() => onOpenChange(false)}
            style={{
              flex: 1,
              height: 46,
              // >= 44px BOTH axes. flex:1 already makes this far wider on any
              // phone; the floor is the law, not a prediction.
              minWidth: 44,
              justifyContent: "center",
              borderRadius: 10,
              fontSize: 13.5,
            }}
          >
            {translate("myMachinery:close")}
          </button>
          <button
            type="button"
            className="btn btn-mod"
            onClick={apply}
            style={{
              flex: 2,
              height: 46,
              minWidth: 44,
              justifyContent: "center",
              borderRadius: 10,
              fontSize: 13.5,
            }}
          >
            {translate("myMachinery:v2.mobile.apply", { count: api.previewCount })}
          </button>
        </>
      }
    >
      <FleetSheetSections api={api} data={data} />
    </SheetV2>
  );
};

export default FleetFilterSheet;
